import {
  Component,
  signal,
  computed,
  inject,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { OpenSearchService } from '../../../../services/open-search.service';
import { ScrollToTextPayload, SearchResult, SnippetPart } from '../../../../models/ScrollToTextPayload';
import { MarkdownStripService } from '../../../../services/markdown-strip.service';
import { TranslateModule } from '@ngx-translate/core';
const SNIPPET_RADIUS = 55;
const MAX_TITLE_LENGTH = 72;
@Component({
  selector: 'app-search-keyword',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule],
  templateUrl: './search-keyword.component.html',
  styleUrl: './search-keyword.component.scss',
})
export class SearchKeywordComponent {
  private openSearchService = inject(OpenSearchService);
  private markdownStrip = inject(MarkdownStripService);
  scrollToText = output<ScrollToTextPayload>();
  query = signal('');
  open = signal(false);
  highlightedIndex = signal(-1);

  private historyResponse = computed(() =>
    this.openSearchService.historyResponse()
  );

  filtered = computed((): SearchResult[] => {
    const q = this.query().toLowerCase().trim();
    if (!q || q.length < 2) return [];

    const history = this.historyResponse();
    if (!history?.openQueryInteractions?.length) return [];

    const results: SearchResult[] = [];

    history.openQueryInteractions.forEach((interaction, index) => {
      const question = interaction.question ?? '';
      const response = interaction.response ?? '';
      const plainResponse = this.markdownStrip.toPlainText(response);
      const lessons = interaction.modeType?.toLowerCase() === 'lessons'
        ? (interaction.modeResponse?.lessons ?? [])
        : [];

      const questionLower = question.toLowerCase();
      const responseLower = plainResponse.toLowerCase();

      const matchInQuestion = questionLower.includes(q);
      const matchInResponse = responseLower.includes(q);
      const lessonText = lessons.map(lesson => [
        lesson.title, lesson.lesson, lesson.description, lesson.finding, lesson.hallazgo,
        lesson.recommendation, lesson.recomendacion, lesson.lessonName, lesson.name,
      ].filter((value): value is string => typeof value === 'string').join(' ')).join('\n');
      const lessonMatchIdx = lessonText.toLowerCase().indexOf(q);

      if (lessonMatchIdx !== -1) {
        const lesson = lessons.find(item => [
          item.title, item.lesson, item.description, item.finding, item.hallazgo,
          item.recommendation, item.recomendacion, item.lessonName, item.name,
        ].some(value => typeof value === 'string' && value.toLowerCase().includes(q)));
        const title = [lesson?.title, lesson?.lessonName, lesson?.name]
          .find((value): value is string => typeof value === 'string' && !!value.trim())
          ?? question;
        results.push({
          interactionIndex: index,
          matchType: 'lesson',
          highlightTitle: this.truncate(title, MAX_TITLE_LENGTH),
          snippetParts: this.buildSnippetParts(lessonText, lessonMatchIdx, q.length, SNIPPET_RADIUS),
          questionId: interaction.question_id,
        });
      }

      if (matchInQuestion && matchInResponse) {
        const questionMatchIdx = questionLower.indexOf(q);
        const responseMatchIdx = responseLower.indexOf(q);
        results.push({
          interactionIndex: index,
          matchType: 'both',
          highlightTitle: this.truncate(question, MAX_TITLE_LENGTH),
          snippetParts: this.buildSnippetParts(plainResponse, responseMatchIdx, q.length, SNIPPET_RADIUS),
          questionId: interaction.question_id,
          questionParts: this.buildSnippetParts(question, questionMatchIdx, q.length),
        });
      } else if (matchInQuestion) {
        const matchIdx = questionLower.indexOf(q);
        results.push({
          interactionIndex: index,
          matchType: 'question',
          highlightTitle: this.truncate(question, MAX_TITLE_LENGTH),
          snippetParts: this.buildSnippetParts(question, matchIdx, q.length),
          questionId: interaction.question_id,
          responsePreview: plainResponse ? this.truncate(plainResponse, 80) : undefined,
        });
      } else if (matchInResponse) {
        const matchIdx = responseLower.indexOf(q);
        results.push({
          interactionIndex: index,
          matchType: 'response',
          highlightTitle: this.truncate(question, MAX_TITLE_LENGTH),
          snippetParts: this.buildSnippetParts(plainResponse, matchIdx, q.length, SNIPPET_RADIUS),
          questionId: interaction.question_id,
        });
      }
    });

    return results.slice(0, 8);
  });

  private truncate(text: string, max: number): string {
    if (text.length <= max) return text;
    return text.slice(0, max).trimEnd() + '…';
  }

  private buildSnippetParts(
    text: string,
    matchStart: number,
    matchLen: number,
    radius = text.length
  ): SnippetPart[] {
    const start = Math.max(0, matchStart - radius);
    const end = Math.min(text.length, matchStart + matchLen + radius);

    const before = (start > 0 ? '…' : '') + text.slice(start, matchStart);
    const match = text.slice(matchStart, matchStart + matchLen);
    const after =
      text.slice(matchStart + matchLen, end) + (end < text.length ? '…' : '');

    return [
      { text: before, highlight: false },
      { text: match, highlight: true },
      { text: after, highlight: false },
    ].filter((p) => p.text.length > 0);
  }

  getTitleParts(title: string): SnippetPart[] {
    const q = this.query().toLowerCase();
    if (!q) return [{ text: title, highlight: false }];
    const idx = title.toLowerCase().indexOf(q);
    if (idx === -1) return [{ text: title, highlight: false }];
    return [
      { text: title.slice(0, idx), highlight: false },
      { text: title.slice(idx, idx + q.length), highlight: true },
      { text: title.slice(idx + q.length), highlight: false },
    ].filter((p) => p.text.length > 0);
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement | null;
    this.query.set(input?.value ?? '');
    this.highlightedIndex.set(-1);
    this.open.set(true);
  }

  onFocus() {
    this.open.set(true);
  }

  onKeydown(event: KeyboardEvent) {
    const results = this.filtered();
    if (!this.open() || !results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightedIndex.set(
        Math.min(this.highlightedIndex() + 1, results.length - 1)
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedIndex.set(Math.max(this.highlightedIndex() - 1, 0));
    } else if (event.key === 'Enter') {
      const idx = this.highlightedIndex();
      if (idx >= 0 && idx < results.length) this.select(results[idx]);
    } else if (event.key === 'Escape') {
      this.open.set(false);
    }
  }

  close() {
    setTimeout(() => this.open.set(false), 150);
  }

  select(item: SearchResult) {
    this.open.set(false);
    this.scrollToText.emit({
      interactionIndex: item.interactionIndex,
      searchQuery: this.query(),
      searchInLessons: item.matchType === 'lesson',
    });
    this.clearQuery();
  }
  clearQuery() {
    this.query.set('');
    this.open.set(false);
  }
}
